import { Suspense } from 'react';

import { Navigate } from '@sorare/routing';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LegalWarningBanner } from '@sorare/core/src/components/jonum/LegalWarningBanner';
import { ANY_SPORT_SERIES } from '@sorare/core/src/constants/__generated__/routes';
import { useDefaultSportPages } from '@sorare/core/src/constants/routes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { SportSwitcher } from '@sorare/core/src/contexts/sport/SportSwitcher';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { lazy } from '@sorare/core/src/lib/retry';
import { sportFromCountry } from '@sorare/core/src/lib/routing/sportFromPath';
import { AppLayout } from '@sorare/gameplay/src/components/navigation/AppLayout';

const NBALanding = lazy(async () => import('@sorare/nba/src/pages/page'));
const FootballLanding = lazy(
  async () => import('@sorare/football/src/pages/page')
);
const MLBLanding = lazy(async () => import('@sorare/baseball/src/pages/page'));

const landings = {
  [Sport.BASEBALL]: MLBLanding,
  [Sport.NBA]: NBALanding,
  [Sport.FOOTBALL]: FootballLanding,
};

const Landing = () => {
  const {
    flags: { landingRedirection, jonumUserInfoCollection = false },
  } = useFeatureFlags();
  const { sport, generateSportContextPath } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const {
    currentLocation: { countryCode },
  } = useConfigContext();
  const isLoggedIn = useIsLoggedIn();
  const defaultSportPages = useDefaultSportPages();
  const showLegalWarning =
    jonumUserInfoCollection && countryCode?.toUpperCase() === 'FR';

  if (isLoggedIn) {
    if (currentUser && !currentUser.anyUserSportProfile?.proUnlocked) {
      return (
        <Navigate
          to={generateSportContextPath(ANY_SPORT_SERIES)}
          replace
          keepSearchParams
        />
      );
    }
    return (
      <Navigate to={`${defaultSportPages[sport]}`} replace keepSearchParams />
    );
  }

  /** Don't use the sport context here because it changes when we navigate to a sport page like /nba */
  const chosenSport = sportFromCountry(landingRedirection as Sport, true);
  const SportLanding = landings[chosenSport];

  return (
    <Suspense>
      <AppLayout>
        <SportLanding isRoot />
        <SportSwitcher sport={chosenSport} />
      </AppLayout>
      {showLegalWarning ? <LegalWarningBanner /> : null}
    </Suspense>
  );
};

export default Landing;
